'use client';

import { useTranslations } from 'next-intl';
import { LocalDateTime } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { KeyDateKey, KeyDateStep } from '@/app/[locale]/event/[eventId]/libs/keyDates';

/** "18 Sept" for entry dates; "Fri 9 Oct" for the event's own days. */
const shortDate = (withWeekday: boolean) => (iso: string, locale: string, timeZone?: string) =>
  new Intl.DateTimeFormat(locale, {
    ...(withWeekday ? { weekday: 'short' as const } : {}),
    day: 'numeric',
    month: 'short',
    timeZone,
  }).format(new Date(iso));

const LONG_LABEL = {
  reg_opens: 'registration.opens',
  reg_closes: 'registration.closes',
  event_starts: 'details.eventStarts',
  event_ends: 'details.eventEnds',
} as const satisfies Record<KeyDateKey, string>;

/**
 * The event's milestones as one strip: side by side on a progress line, or a
 * compact list when the column is too narrow for its text (small phone, large
 * text). The container query is in em, so it follows the reader's text size.
 */
export function EventKeyDates({ steps, locale }: { steps: KeyDateStep[]; locale: string }) {
  return (
    <>
      <div data-key-dates="strip" className="lg:hidden">
        <KeyDatesStrip steps={steps} locale={locale} />
      </div>
      <div data-key-dates="timeline" className="hidden lg:block">
        <KeyDatesTimeline steps={steps} locale={locale} />
      </div>
    </>
  );
}

/** Vertical timeline for desktop: full labels, full dates, a dot per step joined by a line. */
function KeyDatesTimeline({ steps, locale }: { steps: KeyDateStep[]; locale: string }) {
  const t = useTranslations('Event');
  return (
    <ol>
      {steps.map((s, i) => {
        const current = s.status === 'current';
        const past = s.status === 'past';
        const last = i === steps.length - 1;
        return (
          <li
            key={s.key}
            data-timeline-step={s.key}
            aria-current={current ? 'step' : undefined}
            className="relative flex gap-3"
          >
            <div className="flex flex-col items-center" aria-hidden>
              <div
                className={cn(
                  'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full border-2',
                  current
                    ? 'border-accent bg-accent'
                    : past
                      ? 'border-muted-foreground/40 bg-muted-foreground/40'
                      : 'border-muted-foreground/30 bg-transparent',
                )}
              />
              {!last && (
                <div
                  className={cn(
                    'my-0.5 w-px flex-1',
                    past || current ? 'bg-muted-foreground/30' : 'bg-muted-foreground/15',
                  )}
                />
              )}
            </div>
            <div className={cn('min-w-0 flex-1', !last && 'pb-3')}>
              <p
                className={cn(
                  'text-sm leading-tight font-medium',
                  current ? 'text-accent font-semibold' : past ? 'text-muted' : 'text-foreground',
                )}
              >
                {t(LONG_LABEL[s.key])}
              </p>
              <LocalDateTime
                iso={s.iso}
                locale={locale}
                className={cn(
                  'block text-xs leading-tight',
                  current ? 'text-accent/80' : 'text-muted',
                )}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function KeyDatesStrip({ steps, locale }: { steps: KeyDateStep[]; locale: string }) {
  const t = useTranslations('Event');
  const entriesCount = steps.filter((s) => s.group === 'entries').length;
  const eventCount = steps.length - entriesCount;
  const hasEntries = entriesCount > 0;

  return (
    <div className="@container">
      {hasEntries && (
        <div
          className="text-muted mb-2 hidden text-center font-mono text-[0.6875rem] font-semibold tracking-[.12em] uppercase @[20em]:grid"
          style={{ gridTemplateColumns: `${entriesCount}fr ${eventCount}fr` }}
          aria-hidden
        >
          <span>{t('keyDates.entries')}</span>
          <span>{t('keyDates.event')}</span>
        </div>
      )}
      <ol
        className="grid grid-cols-1 gap-y-1 @[20em]:[grid-template-columns:repeat(var(--steps),minmax(0,1fr))]"
        style={{ ['--steps' as string]: steps.length }}
      >
        {steps.map((s, i) => {
          const current = s.status === 'current';
          const done = s.status === 'past';
          return (
            <li
              key={s.key}
              data-key-date={s.key}
              aria-current={current ? 'step' : undefined}
              className="relative flex items-baseline justify-between gap-3 pl-5 @[20em]:block @[20em]:pt-4 @[20em]:pl-0 @[20em]:text-center"
            >
              {/* progress line: horizontal when side by side */}
              <span
                aria-hidden
                className={cn(
                  'absolute top-[3px] hidden h-0.5 @[20em]:block',
                  i === 0
                    ? 'right-0 left-1/2'
                    : i === steps.length - 1
                      ? 'right-1/2 left-0'
                      : 'inset-x-0',
                  done ? 'bg-primary' : 'bg-border',
                )}
              />
              <span
                aria-hidden
                className={cn(
                  'absolute top-1.5 left-0 h-2 w-2 rounded-full border-2 @[20em]:top-0 @[20em]:left-1/2 @[20em]:-ml-1',
                  current
                    ? 'border-accent bg-primary'
                    : done
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/40 bg-card',
                )}
              />
              <span
                className={cn(
                  'font-mono text-[0.6875rem] font-semibold tracking-[.06em] uppercase',
                  current ? 'text-accent' : 'text-muted',
                )}
              >
                <span className="@[20em]:hidden">{t(LONG_LABEL[s.key])}</span>
                <span className="hidden @[20em]:inline">{t(`keyDates.${s.key}`)}</span>
              </span>
              <LocalDateTime
                iso={s.iso}
                locale={locale}
                format={shortDate(s.group === 'event')}
                className={cn(
                  'block text-sm font-semibold whitespace-nowrap',
                  done ? 'text-muted' : 'text-foreground',
                )}
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}
